import { useEffect, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import TeamAuswahl from '../../components/TeamAuswahl';
import { NFL_TEAMS } from '../../data/nflTeams';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Saison-Setup: Deadlines, Sperre, Info-Text, Super Bowl, Spielplan-Import,
// aktive Saison. Alle Writes wie im früheren AdminPanel (merge auf
// saisonKonfiguration/{id}; systemConfig/aktiveSaisons per set).

const SUPER_BOWL_LOGOS = Array.from({ length: 37 }, (_, i) => {
  const nummer = 56 + i;
  const orte = { 56: 'Los Angeles', 57: 'Arizona', 58: 'Las Vegas', 59: 'New Orleans', 60: 'Santa Clara', 61: 'Los Angeles', 62: 'Atlanta', 63: 'New Orleans' };
  return { nummer, jahr: 1966 + nummer, ort: orte[nummer] || 'TBD' };
});

function SaisonSetup({ saisonId, saisonName, config, neuLaden, zeigeToast }) {
  const { userProfile } = useAuth();
  const [laeuft, setLaeuft] = useState(null);
  const [bestaetigen, setBestaetigen] = useState(null);
  const [infoText, setInfoText] = useState('');
  const [sbLogo, setSbLogo] = useState('');
  const [sbGewinner, setSbGewinner] = useState('');
  const [importStatus, setImportStatus] = useState('');

  const deadlinesAktiv = config?.deadlinesAktiv !== undefined ? config.deadlinesAktiv : true;
  const gesperrt = config?.gesperrt === true;

  useEffect(() => {
    setInfoText(config?.infoText || '');
    setSbLogo(config?.superBowlLogo || '');
    setSbGewinner(config?.superBowlGewinner || '');
  }, [config]);

  const schreibe = async (daten, meldung, key) => {
    setLaeuft(key);
    try {
      await setDoc(doc(db, 'saisonKonfiguration', saisonId), {
        ...daten,
        letzteAenderung: new Date().toISOString(),
        geaendertVon: userProfile?.displayName || 'Admin'
      }, { merge: true });
      await neuLaden();
      zeigeToast(meldung);
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setLaeuft(null);
      setBestaetigen(null);
    }
  };

  // Import läuft als Cloud Function (functions/spielplan.js) – dieselbe Logik
  // wie der nächtliche Abgleich. Legt fehlende Spiele an, zieht Kickoffs nach.
  const handleImport = async () => {
    setBestaetigen(null);
    setLaeuft('import');
    setImportStatus('Cloud Function läuft: Spielplan von ESPN abgleichen…');
    try {
      const fn = httpsCallable(getFunctions(undefined, 'europe-west3'), 'spielplanImportieren');
      const r = (await fn({ saisonId })).data;
      const teile = [
        `${r.neu} neu angelegt`,
        `${r.verschoben} Kickoff${r.verschoben === 1 ? '' : 's'} verschoben`,
        `${r.unveraendert} unverändert`
      ];
      if (r.platzhalter) teile.push(`${r.platzhalter} Playoff-Spiele noch ohne Teams (nächster Abgleich)`);
      if (r.uebersprungen) teile.push(`${r.uebersprungen} fehlerhaft übersprungen`);
      setImportStatus(`✅ ${r.gefunden} Spiele bei ESPN (${r.regular} Regular Season, ${r.playoffs} Playoffs, ${r.regularWochen} Wochen): ${teile.join(', ')}.`);
      await neuLaden();
      zeigeToast(r.neu > 0 ? `${r.neu} Spiele angelegt` : 'Spielplan ist aktuell');
    } catch (error) {
      setImportStatus('❌ ' + error.message);
    } finally {
      setLaeuft(null);
    }
  };

  const handleAktiveSaison = async () => {
    setBestaetigen(null);
    setLaeuft('aktiv');
    try {
      const jahr = parseInt(saisonName.split('/')[0], 10);
      const naechste = `${jahr + 1}/${String((jahr + 2) % 100).padStart(2, '0')}`;
      await setDoc(doc(db, 'systemConfig', 'aktiveSaisons'), {
        aktiveSaison: saisonName,
        naechsteSaison: naechste,
        letztesUpdate: new Date().toISOString()
      });
      zeigeToast(`Aktive Saison: ${saisonName}`);
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setLaeuft(null);
    }
  };

  const bestaetigung = (text, onJa) => (
    <span className="bestaetigen">
      {text}
      <button type="button" className="aktion" onClick={onJa}>Ja</button>
      <button type="button" className="aktion aktion--neutral" onClick={() => setBestaetigen(null)}>Nein</button>
    </span>
  );

  return (
    <>
      {/* Deadlines */}
      <div className={`karte${deadlinesAktiv ? '' : ' karte--gelb'}`}>
        <div className="schalter-zeile">
          <div>
            <h2>⏰ Deadlines</h2>
            <p className="karte__hinweis" style={{ margin: 0 }}>
              An: Tipps nur bis Kickoff, Bonusfragen bis zur jeweiligen Deadline. Aus: alles offen – nur für Tests.
            </p>
          </div>
          <button type="button" className={`schalter-button${deadlinesAktiv ? ' ist-an' : ''}`} disabled={laeuft === 'deadlines'}
            onClick={() => schreibe({ deadlinesAktiv: !deadlinesAktiv }, `Deadlines ${!deadlinesAktiv ? 'aktiviert' : 'deaktiviert'}`, 'deadlines')}>
            {deadlinesAktiv ? 'AN' : 'AUS'}
          </button>
        </div>
      </div>

      {/* Sperre */}
      <div className={`karte${gesperrt ? ' karte--rot' : ''}`}>
        <div className="schalter-zeile">
          <div>
            <h2>🔒 Saison sperren</h2>
            <p className="karte__hinweis" style={{ margin: 0 }}>
              Gesperrt = archiviert: keine Tipps, keine Bonusfragen, nur noch Anzeige. Am Saisonende nach der Auswertung.
            </p>
          </div>
          {bestaetigen === 'sperre' ? (
            bestaetigung(gesperrt ? 'Wieder öffnen?' : 'Wirklich sperren?', () => schreibe({ gesperrt: !gesperrt }, gesperrt ? 'Saison geöffnet' : 'Saison gesperrt', 'sperre'))
          ) : (
            <button type="button" className={`schalter-button${gesperrt ? ' ist-an' : ''}`} style={gesperrt ? { background: '#dc3545', borderColor: '#dc3545' } : undefined}
              onClick={() => setBestaetigen('sperre')} disabled={laeuft === 'sperre'}>
              {gesperrt ? 'GESPERRT' : 'OFFEN'}
            </button>
          )}
        </div>
      </div>

      {/* Phasen */}
      <div className="karte">
        <h2>🏈 Saisonphasen</h2>
        <p className="karte__hinweis" style={{ margin: 0 }}>
          Regular Season: <strong>{config?.regularSeasonWeeks || 18} Wochen</strong>
          {config?.regularSeasonWeeks ? ' (aus dem Spielplan-Import)' : ' (Standard, noch kein Import)'}.
          Ob ein Spiel Regular Season oder Playoff ist, steht im Spiel-Doc und kommt von ESPN. Manuell ist hier nichts einzustellen.
        </p>
      </div>

      {/* Info-Text */}
      <div className="karte">
        <h2>ℹ️ Info-Text auf der Saisonauswahl</h2>
        <p className="karte__hinweis">Kurzer Hinweis auf der Saison-Karte, z. B. „Testsaison“ oder „Playoffs ab 10.01.“.</p>
        <textarea className="textarea-voll" value={infoText} onChange={e => setInfoText(e.target.value)} placeholder="Kein Text" />
        <div className="aktion-zeile">
          <button type="button" className="aktion" disabled={laeuft === 'info' || infoText === (config?.infoText || '')}
            onClick={() => schreibe({ infoText }, 'Info-Text gespeichert', 'info')}>
            💾 Speichern
          </button>
        </div>
      </div>

      {/* Super Bowl */}
      <div className="karte">
        <h2>🏆 Super Bowl</h2>
        <p className="karte__hinweis">Logo der Saison und – nach dem Sperren – der Champion. Beides erscheint auf der Saisonauswahl.</p>
        <label className="feld">
          <span className="feld__label">Super-Bowl-Logo</span>
          <select className="select-voll" value={sbLogo} onChange={e => setSbLogo(e.target.value)}>
            <option value="">– kein Logo –</option>
            {SUPER_BOWL_LOGOS.map(sb => (
              <option key={sb.nummer} value={`/images/superbowl/sb${sb.nummer}.png`}>Super Bowl {sb.nummer} ({sb.jahr}) – {sb.ort}</option>
            ))}
          </select>
        </label>
        {sbLogo && (
          <div className="logo-vorschau">
            <img src={sbLogo} alt="" onError={e => { e.target.style.display = 'none'; }} />
          </div>
        )}
        {gesperrt ? (
          <div style={{ marginTop: 12 }}>
            <TeamAuswahl label="Super-Bowl-Champion" value={sbGewinner} teams={Object.keys(NFL_TEAMS)} onChange={setSbGewinner} platzhalter="Champion wählen" />
          </div>
        ) : (
          <p className="karte__hinweis" style={{ marginTop: 10 }}>Der Champion lässt sich eintragen, sobald die Saison gesperrt ist.</p>
        )}
        <div className="aktion-zeile">
          <button type="button" className="aktion" disabled={laeuft === 'sb'}
            onClick={() => schreibe({
              superBowlLogo: sbLogo,
              superBowlGewinner: sbGewinner,
              superBowlGewinnerLogo: sbGewinner ? (NFL_TEAMS[sbGewinner]?.logo || '') : ''
            }, 'Super-Bowl-Daten gespeichert', 'sb')}>
            💾 Speichern
          </button>
        </div>
      </div>

      {/* Import */}
      <div className="karte karte--blau">
        <h2>📥 Spielplan-Import (ESPN)</h2>
        <p className="karte__hinweis">
          Gleicht die Saison {saisonName} mit ESPN ab: fehlende Spiele werden angelegt, verschobene Kickoffs nachgezogen. Ergebnisse, Wochen und Tipps bleiben unberührt. Läuft automatisch jede Nacht um 5 Uhr – der Knopf ist für den Notfall.
        </p>
        <div className="aktion-zeile">
          {bestaetigen === 'import' ? (
            bestaetigung(`Spielplan ${saisonName} jetzt importieren?`, handleImport)
          ) : (
            <button type="button" className="aktion" onClick={() => setBestaetigen('import')} disabled={laeuft !== null}>
              {laeuft === 'import' ? '⏳ Importiere…' : '📥 Spielplan importieren'}
            </button>
          )}
        </div>
        {importStatus && <div className="status-text">{importStatus}</div>}
      </div>

      {/* Aktive Saison */}
      <div className="karte karte--gelb">
        <h2>⚙️ Aktive Saison für die Automatik</h2>
        <p className="karte__hinweis">
          Die Cloud Functions (Scores, Punkte, Erinnerungen) arbeiten nur auf der aktiven Saison. Beim Saisonwechsel hier umstellen.
        </p>
        <div className="aktion-zeile">
          {bestaetigen === 'aktiv' ? (
            bestaetigung(`${saisonName} als aktive Saison setzen?`, handleAktiveSaison)
          ) : (
            <button type="button" className="aktion" onClick={() => setBestaetigen('aktiv')} disabled={laeuft !== null}>
              {laeuft === 'aktiv' ? '⏳…' : `⚙️ ${saisonName} aktiv setzen`}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default SaisonSetup;
